'use client';

import { useGetAllCountries } from '@/backend/common/get-all-countries';
import { useUpdateKYCLevel1 } from '@/backend/onboarding/post-update-kyc-level-1';
import { useUpdateStep } from '@/backend/onboarding/post-update-step';
import { useUpdateUser } from '@/backend/user/post-update-user';
import Buttons_Over18 from '@/components/Inputs/Buttons_Over18/Buttons_Over18';
import Input_CheckBox_Attestation from '@/components/Inputs/Input_CheckBox_Attestation/Input_CheckBox_Attestation';
import Input_CheckBox_KYC_Notice from '@/components/Inputs/Input_CheckBox_KYC_Notice/Input_CheckBox_KYC_Notice';
import Input_Country from '@/components/Inputs/Input_Country/Input_Country';
import Input_FirstName from '@/components/Inputs/Input_FirstName/Input_FirstName';
import Input_LastName from '@/components/Inputs/Input_LastName/Input_LastName';
import Loader_Form_Primary from '@/components/Loaders/Loader_Form_Primary/Loader_Form_Primary';
import { icons } from '@/Icons';
import { useModalStoreActions, useUser } from '@/store';
import ds from '@/styles/ds/ds.module.scss';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

export interface UserInfo {
	firstName: string;
	lastName: string;
	countryCode: string;
	attestation: boolean;
}

export enum VerificationLevel1FormIds {
	FirstName = 'firstName',
	LastName = 'lastName',
	Country = 'country',
	Attestation = 'attestation',
	AcknowledgedKYCNotice = 'acknowledgedKYCNotice',
}

interface VerificationLevel1Props {
	isOnboarding?: boolean;
	isInModal?: boolean;
}
export type ValidateField = (
	field: 'firstName' | 'lastName' | 'isAtLeast18',
	isAtLeast18?: boolean | null
) => void;

const VerificationLevel1 = ({
	isOnboarding,
	isInModal,
}: VerificationLevel1Props) => {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [country, setCountry] = useState('');
	const [postalCode, setPostalCode] = useState('');
	const [attestation, setAttestation] = useState(false);
	const [acknowledgedKYCNotice, setAcknowledgedKYCNotice] = useState(false);
	const [isAtLeast18, setIsAtLeast18] = useState<boolean | null>(null);

	const { data: countries, getAllCountries } = useGetAllCountries();
	useEffect(() => {
		getAllCountries();
	}, []);

	const user = useUser();
	const [errors, setErrors] = useState<{ [key: string]: string }>({});

	const { updateUser, loading: loading_updateUser } = useUpdateUser();
	const { updateKYCLevel1, loading: loading_updateKYCLevel1 } =
		useUpdateKYCLevel1();
	const { updateStep, loading: loading_updateStep } = useUpdateStep();
	const { setModalStatus } = useModalStoreActions();

	const isLoading = useMemo(() => {
		return loading_updateUser || loading_updateKYCLevel1 || loading_updateStep;
	}, [loading_updateUser, loading_updateKYCLevel1, loading_updateStep]);

	/* -------------------------------------------------------------------------- */
	const formRef = useRef<HTMLFormElement | null>(null);
	const [canSubmit, setCanSubmit] = useState(false);

	useEffect(() => {
		if (!user || !countries) return;

		const form = formRef.current;
		if (!form) return;

		const required = [
			VerificationLevel1FormIds.FirstName,
			VerificationLevel1FormIds.LastName,
			VerificationLevel1FormIds.Country,
			VerificationLevel1FormIds.Attestation,
			VerificationLevel1FormIds.AcknowledgedKYCNotice,
		];

		const allFieldsFilled = required.every((field) => {
			const el = form.elements.namedItem(field) as
				| HTMLInputElement
				| HTMLSelectElement;
			return el?.type === 'checkbox'
				? (el as HTMLInputElement).checked
				: el?.value.trim() !== '';
		});
		const isFormUpdated =
			firstName !== user.firstName ||
			lastName !== user.lastName ||
			country !== user.country?.code ||
			attestation !== user.moreDetails?.attestation;

		setCanSubmit(
			allFieldsFilled && isFormUpdated && Object.keys(errors).length === 0
		);
	}, [
		firstName,
		lastName,
		attestation,
		country,
		acknowledgedKYCNotice,
		user,
		errors,
	]);

	/* -------------------------------------------------------------------------- */
	const elementError = (name: string) => {
		const el = document.querySelector<HTMLInputElement>(`[name="${name}"]`);
		el?.classList.add('!border-tokens-negative');
	};

	const elementSuccess = (name: string) => {
		const el = document.querySelector<HTMLInputElement>(`[name="${name}"]`);
		el?.classList.remove('!border-tokens-negative');
	};

	const setFieldError = (field: string, message: string) => {
		setErrors((prev) => ({ ...prev, [field]: message }));
	};

	const clearFieldError = (field: string) => {
		setErrors((prev) => {
			const { [field]: _, ...rest } = prev;
			return rest;
		});
	};

	const getFieldError = (
		field: 'firstName' | 'lastName' | 'isAtLeast18',
		value: string | boolean | null
	): string | null => {
		switch (field) {
			case 'firstName':
				if (!value || (typeof value === 'string' && value.length < 2)) {
					return 'First name must be at least 2 characters.';
				}
				break;
			case 'lastName':
				if (!value || (typeof value === 'string' && value.length < 2)) {
					return 'Last name must be at least 2 characters.';
				}
				break;
			case 'isAtLeast18':
				if (value === null) return 'You must confirm your age.';
				if (value === false)
					return 'You must be at least 18 years old to submit this form.';
				break;
			default:
				return null;
		}
		return null;
	};

	const validateAllFields = () => {
		const newErrors: { [key: string]: string } = {};
		const firstNameError = getFieldError('firstName', firstName);
		const lastNameError = getFieldError('lastName', lastName);
		const ageError = getFieldError('isAtLeast18', isAtLeast18);

		if (firstNameError) newErrors.firstName = firstNameError;
		if (lastNameError) newErrors.lastName = lastNameError;
		if (ageError) newErrors.isAtLeast18 = ageError;

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const validateField = (
		field: 'firstName' | 'lastName' | 'isAtLeast18',
		value?: string | boolean | null
	) => {
		let errorMsg: string | null = null;
		if (field === 'firstName') {
			errorMsg = getFieldError('firstName', value ?? firstName);
			if (errorMsg) {
				elementError(VerificationLevel1FormIds.FirstName);
				setFieldError('firstName', errorMsg);
			} else {
				elementSuccess(VerificationLevel1FormIds.FirstName);
				clearFieldError('firstName');
			}
		}
		if (field === 'lastName') {
			errorMsg = getFieldError('lastName', value ?? lastName);
			if (errorMsg) {
				elementError(VerificationLevel1FormIds.LastName);
				setFieldError('lastName', errorMsg);
			} else {
				elementSuccess(VerificationLevel1FormIds.LastName);
				clearFieldError('lastName');
			}
		}
		if (field === 'isAtLeast18') {
			errorMsg = getFieldError('isAtLeast18', value ?? isAtLeast18);
			if (errorMsg) {
				if (value === false) elementError('isAtLeast18');
				setFieldError('isAtLeast18', errorMsg);
			} else {
				clearFieldError('isAtLeast18');
			}
		}
	};

	/* -------------------------------------------------------------------------- */

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Clear previous errors
		setErrors({});

		// Validate all fields at once
		const isValid = validateAllFields();
		if (!isValid) return;

		const formData = new FormData(e.currentTarget);
		const formValues: Record<string, string> = {};

		formData.forEach((value, key) => {
			formValues[key] =
				key === VerificationLevel1FormIds.Attestation ? 'on' : value.toString();
		});

		// --- Remaining Field Validations (except postal code already done) ---
		const validations: Record<string, (value: string) => string | null> = {
			[VerificationLevel1FormIds.Country]: (v) =>
				v.trim() !== '' ? null : 'Country is required.',
			[VerificationLevel1FormIds.Attestation]: (v) =>
				v === 'on' ? null : 'You must accept the attestation.',
		};

		let hasExtraError = false;
		Object.entries(validations).forEach(([key, validate]) => {
			const value = formValues[key] || '';
			const errorMessage = validate(value);
			if (errorMessage) {
				elementError(key);
				setErrors((prev) => ({ ...prev, [key]: errorMessage }));
				hasExtraError = true;
			} else {
				elementSuccess(key);
			}
		});
		if (hasExtraError) return;

		// --- Build Payload ---
		const body: UserInfo = {
			firstName,
			lastName,
			countryCode: country,
			attestation: true,
		};

		if (isOnboarding) {
			const filteredBody = Object.fromEntries(
				Object.entries(body).filter(([_, value]) => value !== '')
			) as UserInfo;

			await updateKYCLevel1(filteredBody);
		} else {
			const body: any = {
				firstName,
				lastName,
				countryCode: country,
				attestation: true,
			};

			await updateUser({ body });
		}
	};

	return (
		<form
			ref={formRef}
			onSubmit={handleSubmit}
			className="my-auto gap-4 h-full overflow-hidden"
		>
			{isLoading && (
				<div className="w-full h-full left-0 top-0 absolute z-10 backdrop-blur-sm grid place-items-center">
					<Loader_Form_Primary divProps={{ className: '!w-[25%] !h-[25%]' }} />
				</div>
			)}
			{isOnboarding ? (
				<p className={`${ds.sectionHeader} flex items-center gap-3`}>
					<span className="w-6 h-6 relative text-dove-white-o-70 ">
						<icons.verification />
					</span>
					Verify Level 1
				</p>
			) : null}
			<div
				className={`grid my-auto  overflow-auto ${ds.scrollbar_thin}  ${isInModal ? 'h-full' : 'max-h-[65vh]'} ${isLoading ? 'opacity-0' : ''}`}
				style={{ gridTemplateRows: '1fr min-content' }}
			>
				<div className="relative  h-full">
					<div
						className={`overflow-scroll ${isInModal ? 'md:h-[500px]' : 'h-full'} pr-2 ${ds.scrollbar_thin}  ${ds.form_primary} !gap-5`}
					>
						<p>
							You must confirm your age and country of residence before
							depositing.
						</p>
						<div className="flex gap-2 items-end">
							<Buttons_Over18
								isAtLeast18={isAtLeast18}
								setIsAtLeast18={setIsAtLeast18}
								validateField={validateField}
							/>
						</div>
						<div className="flex gap-2 items-end">
							<Input_FirstName
								firstName={firstName}
								setFirstName={setFirstName}
								validateField={validateField}
							/>
							<Input_LastName
								lastName={lastName}
								setLastName={setLastName}
								validateField={validateField}
							/>
						</div>

						<div className="flex gap-2 items-end">
							<Input_Country
								countries={countries}
								country={country}
								setCountry={setCountry}
								postalCode={postalCode}
								setPostalCode={setPostalCode}
							/>
						</div>

						<div className="flex flex-col gap-4">
							<Input_CheckBox_Attestation
								attestation={attestation}
								setAttestation={setAttestation}
							/>

							<Input_CheckBox_KYC_Notice
								acknowledgedKYCNotice={acknowledgedKYCNotice}
								setAcknowledgedKYCNotice={setAcknowledgedKYCNotice}
							/>
						</div>
					</div>
				</div>
				{!isOnboarding ? (
					<div
						className={`flex flex-col gap-3 pt-4 sticky bottom-0 ${isInModal ? 'bg-midnight-blue' : 'bg-custom-extra-1'}`}
					>
						<button
							disabled={isLoading || !canSubmit}
							aria-label="Accept and Continue"
							type="submit"
							className={`${ds.primary} ${ds.purpleWhite} h-10 !px-4`}
						>
							Submit
						</button>
						{/* <button
									aria-label="Skip"
									type="button"
									onClick={(e) => {
										e.preventDefault();
										setModalStatus(null);
									}}
									className={`${ds.tertiary} ${ds.purpleWhite} h-10 !px-4 max-w-[150px]`}
								>
									close
								</button> */}

						{Object.keys(errors).length > 0 && (
							<div className="px-5 py-2.5 bg-tokens-negative-o-20 rounded-lg flex gap-2.5">
								<icons.infoerror className="w-5 h-5 text-tokens-negative" />
								<p className="text-xs text-tokens-negative pt-0.5">
									{errors[Object.keys(errors)[0]]}
								</p>
							</div>
						)}
					</div>
				) : null}
				{isOnboarding ? (
					<div className="flex flex-col gap-2 mt-4 sticky bottom-0 bg-midnight-blue">
						<button
							disabled={isLoading || !canSubmit}
							aria-label="Accept and Continue"
							type="submit"
							className={`${ds.primary} ${ds.purpleWhite} h-10 !px-4`}
						>
							Submit
						</button>
						<button
							aria-label="Skip"
							type="button"
							onClick={(e) => {
								e.preventDefault();
								updateStep(user?.currentOnboardingStep);
							}}
							className=" w-min mx-auto motion-safe:hover:underline underline-offset-2"
						>
							Skip
						</button>
						{Object.keys(errors).length > 0 && (
							<div className="px-5 py-2.5 bg-tokens-negative-o-20 rounded-lg flex gap-2.5">
								<icons.infoerror className="w-5 h-5 text-tokens-negative" />
								<p className="text-xs text-tokens-negative pt-0.5">
									{errors[Object.keys(errors)[0]]}
								</p>
							</div>
						)}
					</div>
				) : null}
			</div>
		</form>
	);
};

export default VerificationLevel1;
